/**
 * French social security number (numéro d'inscription au répertoire).
 */

/**
 * Extract gender from a social security number.
 * The first digit indicates gender:
 * - 1 = male
 * - 2 = female
 * - Other values = invalid/unable to determine
 */
export function extractGenderFromSocialSecurityNumber(
  socialSecurityNumber: string | null | undefined
): "male" | "female" | null {
  if (!socialSecurityNumber) {
    return null;
  }

  // Keep digits only. The first digit is the gender.
  const cleanedSocialSecurityNumber = socialSecurityNumber.replace(/\D/g, "");

  // Need at least 1 digit to determine gender
  if (cleanedSocialSecurityNumber.length === 0) {
    return null;
  }

  const firstDigit = cleanedSocialSecurityNumber[0];

  if (firstDigit === "1") {
    return "male";
  }

  if (firstDigit === "2") {
    return "female";
  }

  // Invalid or unknown gender indicator
  return null;
}

/**
 * Validates that a string contains only digits
 */
function isDigits(str: string): boolean {
  return str.length > 0 && /^\d+$/.test(str);
}

/**
 * Validates department code format (01-99, 2A, or 2B)
 */
function isValidDepartmentCode(dept: string, allowCorsica: boolean): boolean {
  if (allowCorsica && (dept === "2A" || dept === "2B")) {
    return true;
  }
  if (!isDigits(dept)) {
    return false;
  }
  return true;
}

/**
 * Validates the common structure shared by a social security number and an NIA
 * Format: S AA MM DD CCC NNN
 */
function validateCommonStructure(input: string, allowedSexeDigits: readonly string[], allowCorsica: boolean): boolean {
  // Position 0: Sexe
  const sexe = input[0];
  if (!sexe) {
    return false;
  }
  if (!allowedSexeDigits.includes(sexe)) {
    return false;
  }

  // Positions 1-2: Année de naissance (AA)
  const annee = input.slice(1, 3);
  if (!isDigits(annee)) {
    return false;
  }

  // Positions 3-4: Mois de naissance (MM)
  const mois = input.slice(3, 5);
  if (!isDigits(mois)) {
    return false;
  }

  // Positions 5-6: Département (DD)
  const departement = input.slice(5, 7);
  if (!isValidDepartmentCode(departement, allowCorsica)) {
    return false;
  }

  // Positions 7-9: Commune (CCC)
  const commune = input.slice(7, 10);
  if (!isDigits(commune)) {
    return false;
  }

  // Positions 10-12: Numéro d'inscription (NNN)
  const numeroInscription = input.slice(10, 13);
  if (!isDigits(numeroInscription)) {
    return false;
  }

  return true;
}

/**
 * Validates a French social security number.
 * Format: S AA MM DD CCC NNN [KK]
 * - S (1 digit): sexe (1 or 2)
 * - AA (2 digits): année de naissance (00-99)
 * - MM (2 digits): mois de naissance
 * - DD (2 chars): département (01-99, 2A, or 2B)
 * - CCC (3 digits): commune (001-999)
 * - NNN (3 digits): numéro d'inscription (001-999)
 * - KK (2 digits, optional): clé de contrôle (00-99)
 * Accepts 13 or 15 characters (the last 2 are optional)
 */
export function isValidSocialSecurityNumber(socialSecurityNumber: string | null | undefined): boolean {
  if (!socialSecurityNumber) {
    return false; // Not a valid format
  }

  // Must be 13 or 15 characters
  if (socialSecurityNumber.length !== 13 && socialSecurityNumber.length !== 15) {
    return false;
  }

  // Validate common structure (first 13 characters)
  if (!validateCommonStructure(socialSecurityNumber, ["1", "2"], true)) {
    return false;
  }

  // If 15 characters, validate clé de contrôle (KK) - positions 13-14
  if (socialSecurityNumber.length === 15) {
    const cleControle = socialSecurityNumber.slice(13, 15);
    if (!isDigits(cleControle)) {
      return false;
    }
  }

  return true;
}

/**
 * Validates NIA (Numéro d'Identification d'Attributaire) format
 * Format: S AA MM DD CCC NNN
 * - S (1 digit): sexe (3, 4, 7, or 8 for temporary number)
 * - AA (2 digits): année de naissance (00-99)
 * - MM (2 digits): mois de naissance
 * - DD (2 chars): département (01-99, 2A, or 2B)
 * - CCC (3 digits): commune (001-999)
 * - NNN (3 digits): numéro d'inscription (001-999)
 * Always 13 characters (no control key)
 */
export function isValidNIA(nia: string | null | undefined): boolean {
  if (!nia) {
    return false; // Not a valid format
  }

  // Must be exactly 13 characters
  if (nia.length !== 13) {
    return false;
  }

  // Validate common structure (supports Corsica codes)
  return validateCommonStructure(nia, ["3", "4", "7", "8"], true);
}

export type SocialSecurityNumberType = "NIR" | "NIA" | "NTT";

/**
 * Validates NTT (Numéro Temporaire de Travailleur) format
 * Format: S AA MM DD [rest...]
 * - S (1 digit): sexe (1 or 2)
 * - AA (2 digits): année de naissance (00-99)
 * - MM (2 digits): mois de naissance
 * - DD (2 chars): département (01-99, 2A, or 2B)
 * - [rest...]: reste variable (identifiant unique, etc.) - optional
 * Accepts 7 to 40 characters (minimum is 7: genre + année + mois + département)
 */
export function isValidNTT(ntt: string | null | undefined): boolean {
  if (!ntt) {
    return false;
  }

  // Must be between 7 and 40 characters
  if (ntt.length < 7 || ntt.length > 40) {
    return false;
  }

  // Position 0: Sexe (1 or 2)
  const sexe = ntt[0];
  if (sexe !== "1" && sexe !== "2") {
    return false;
  }

  // Positions 1-2: Année de naissance (AA)
  const annee = ntt.slice(1, 3);
  if (!isDigits(annee)) {
    return false;
  }

  // Positions 3-4: Mois de naissance (MM)
  const mois = ntt.slice(3, 5);
  if (!isDigits(mois)) {
    return false;
  }

  // Positions 5-6 or 5-7: Département (DD)
  // Check if it's Corsica (2A or 2B) - positions 5-6
  if (ntt.length >= 7 && (ntt.slice(5, 7) === "2A" || ntt.slice(5, 7) === "2B")) {
    // Valid Corsica code, rest can be anything
    return true;
  }

  // Check if it's a numeric department (01-99) - 2 characters
  if (ntt.length >= 7) {
    const departement = ntt.slice(5, 7);
    if (isValidDepartmentCode(departement, false)) {
      // Valid numeric department, rest can be anything
      return true;
    }
  }

  return false;
}

/**
 * Cleans a social security number string by keeping only digits and A/B letters, converting to uppercase.
 * Removes all other characters (spaces, dashes, dots, etc.)
 *
 * @param value - The input string to clean
 * @returns The cleaned string with only digits and A/B in uppercase
 */
export function cleanSocialSecurityNumber(value: string): string {
  // Keep only digits and A/B, convert to uppercase
  return value.replace(/[^0-9AB]/gi, "").toUpperCase();
}

/**
 * INSEE control key: `KK = 97 − (the 13-character number mod 97)`.
 *
 * Corsica department codes are letters, so the 13-character number is not a pure integer.
 * INSEE documents two equivalent rewrites before the modulo:
 * - replace `A` with `0` and subtract 1 000 000 (`2B`: subtract 2 000 000), or
 * - replace `2A` with `19` and `2B` with `18`, and stop there.
 * This function uses the second form. Subtracting again would apply the correction twice.
 */
export function computeSocialSecurityNumberControlKey(socialSecurityNumber: string): string | null {
  if (socialSecurityNumber.length !== 13 || !isValidSocialSecurityNumber(socialSecurityNumber)) {
    return null;
  }

  const department = socialSecurityNumber.slice(5, 7);
  let numericSocialSecurityNumber = socialSecurityNumber;
  if (department === "2A") {
    numericSocialSecurityNumber = `${socialSecurityNumber.slice(0, 5)}19${socialSecurityNumber.slice(7)}`;
  } else if (department === "2B") {
    numericSocialSecurityNumber = `${socialSecurityNumber.slice(0, 5)}18${socialSecurityNumber.slice(7)}`;
  }

  if (!/^\d{13}$/.test(numericSocialSecurityNumber)) {
    return null;
  }

  const remainder = Number(BigInt(numericSocialSecurityNumber) % 97n);
  return String(97 - remainder).padStart(2, "0");
}

export function isValidSocialSecurityNumberControlKey(socialSecurityNumber: string | null | undefined): boolean {
  if (
    !socialSecurityNumber ||
    socialSecurityNumber.length !== 15 ||
    !isValidSocialSecurityNumber(socialSecurityNumber)
  ) {
    return false;
  }

  const expectedControlKey = computeSocialSecurityNumberControlKey(socialSecurityNumber.slice(0, 13));
  return expectedControlKey !== null && socialSecurityNumber.slice(13, 15) === expectedControlKey;
}

/**
 * Turns a raw OCR string into a 15-character social security number with a valid control key, or null.
 * Only a complete 15-character number whose key already matches is accepted — a
 * 13-character reading is rejected so we never invent a checksum for a misread number.
 */
export function toValidSocialSecurityNumberWithControlKey(
  rawSocialSecurityNumber: string | null | undefined
): string | null {
  if (!rawSocialSecurityNumber) {
    return null;
  }

  const cleanedSocialSecurityNumber = cleanSocialSecurityNumber(rawSocialSecurityNumber);
  return isValidSocialSecurityNumberControlKey(cleanedSocialSecurityNumber) ? cleanedSocialSecurityNumber : null;
}

/**
 * Constants for social security number formatting structure
 */
const FORMAT_CONSTANTS = {
  SEX_START: 0,
  SEX_LENGTH: 1,
  YEAR_START: 1,
  YEAR_LENGTH: 2,
  MONTH_START: 3,
  MONTH_LENGTH: 2,
  DEPT_START: 5,
  DEPT_LENGTH: 2,
  CITY_LENGTH: 3,
  NUMBER_LENGTH: 3,
  CONTROL_KEY_LENGTH: 2,
  MIN_LENGTH_FOR_FORMATTING: 10,
  COMPLETE_LENGTH: 13,
  FULL_LENGTH: 15,
  GROUP_SIZE_SHORT: 3,
  GROUP_SIZE_LONG: 4,
} as const;

/**
 * Groups a string by chunks of specified size
 */
function groupBySize(text: string, groupSize: number): string {
  const regex = new RegExp(`.{1,${groupSize}}`, "g");
  return text.match(regex)?.join(" ") || text;
}

/**
 * Formats the common structure (first 7 characters: sexe, année, mois, département)
 */
function formatCommonStructure(cleaned: string, startIndex: number): { parts: string[]; nextIndex: number } {
  const parts: string[] = [];
  let index = startIndex;

  // Sexe (1 char)
  if (cleaned.length > index) {
    parts.push(cleaned.slice(index, index + FORMAT_CONSTANTS.SEX_LENGTH));
    index += FORMAT_CONSTANTS.SEX_LENGTH;
  }

  // Année (2 chars)
  if (cleaned.length > index) {
    parts.push(cleaned.slice(index, index + FORMAT_CONSTANTS.YEAR_LENGTH));
    index += FORMAT_CONSTANTS.YEAR_LENGTH;
  }

  // Mois (2 chars)
  if (cleaned.length > index) {
    parts.push(cleaned.slice(index, index + FORMAT_CONSTANTS.MONTH_LENGTH));
    index += FORMAT_CONSTANTS.MONTH_LENGTH;
  }

  // Département (2 chars, can be 2A or 2B for Corsica)
  if (cleaned.length > index) {
    parts.push(cleaned.slice(index, index + FORMAT_CONSTANTS.DEPT_LENGTH));
    index += FORMAT_CONSTANTS.DEPT_LENGTH;
  }

  return { parts, nextIndex: index };
}

/**
 * Formats the commune, registration number, and control key up to maxLength
 */
function formatSocialSecurityNumberSection(
  cleaned: string,
  startIndex: number,
  maxLength: number
): { parts: string[]; nextIndex: number } {
  const parts: string[] = [];
  let index = startIndex;

  // Commune (3 chars) - only if we haven't exceeded maxLength yet
  if (cleaned.length > index && index + FORMAT_CONSTANTS.CITY_LENGTH <= maxLength) {
    parts.push(cleaned.slice(index, index + FORMAT_CONSTANTS.CITY_LENGTH));
    index += FORMAT_CONSTANTS.CITY_LENGTH;
  }

  // Numéro (3 chars) - only if we haven't exceeded maxLength yet
  if (cleaned.length > index && index + FORMAT_CONSTANTS.NUMBER_LENGTH <= maxLength) {
    parts.push(cleaned.slice(index, index + FORMAT_CONSTANTS.NUMBER_LENGTH));
    index += FORMAT_CONSTANTS.NUMBER_LENGTH;
  }

  // Control key (2 chars) when the number is at least 15 characters
  if (
    cleaned.length >= FORMAT_CONSTANTS.FULL_LENGTH &&
    cleaned.length > index &&
    index + FORMAT_CONSTANTS.CONTROL_KEY_LENGTH <= maxLength
  ) {
    parts.push(cleaned.slice(index, index + FORMAT_CONSTANTS.CONTROL_KEY_LENGTH));
    index += FORMAT_CONSTANTS.CONTROL_KEY_LENGTH;
  }

  return { parts, nextIndex: index };
}

/**
 * Determines if grouped formatting should be skipped (special Corsica cases)
 */
function shouldSkipSocialSecurityNumberFormatting(
  dept: string,
  remainingAfterDept: number,
  totalLength: number
): boolean {
  const isCorsica = dept === "2A" || dept === "2B";
  if (!isCorsica) {
    return false;
  }

  // Case 1: Corsica with exactly 4 remaining chars AND length < 13
  if (remainingAfterDept === 4 && totalLength < FORMAT_CONSTANTS.COMPLETE_LENGTH) {
    return true;
  }

  // Case 2: Corsica with length > 15 (group by 4 instead)
  if (totalLength > FORMAT_CONSTANTS.FULL_LENGTH) {
    return true;
  }

  return false;
}

/**
 * Formats the remaining characters after the grouped section
 */
function formatRemainingChars(rest: string, totalLength: number): string {
  if (totalLength < FORMAT_CONSTANTS.MIN_LENGTH_FOR_FORMATTING) {
    // For strings < 10 chars, group by 3
    // But for strings with exactly 4 remaining chars, don't split
    if (rest.length === 4) {
      return rest;
    }
    return groupBySize(rest, FORMAT_CONSTANTS.GROUP_SIZE_SHORT);
  }

  if (totalLength > FORMAT_CONSTANTS.FULL_LENGTH) {
    // For strings > 15 chars, group by 4 (readability)
    return groupBySize(rest, FORMAT_CONSTANTS.GROUP_SIZE_LONG);
  }

  // For strings of 10-15 chars that skipped grouped formatting (Corsica case with 4 remaining)
  // Keep the 4 chars together, otherwise apply progressive formatting
  if (rest.length === 4) {
    return rest;
  }
  return groupBySize(rest, FORMAT_CONSTANTS.GROUP_SIZE_SHORT);
}

/**
 * Formats a social security number string according to the common structure.
 * Format based on the common structure (first 7 chars) + the grouped number (up to 15) + the rest as-is
 * - Common: 1 95 07 22 (sexe-année-mois-département)
 * - Number: + 333 444 78 (commune-numéro-clé)
 * - NTT: + reste tel quel si > 15 chars
 *
 * @param value - The input string to format (should already be cleaned)
 * @returns The formatted string with spaces between groups
 */
export function formatSocialSecurityNumber(value: string): string {
  const cleaned = cleanSocialSecurityNumber(value || "");
  if (!cleaned) return "";

  const allParts: string[] = [];
  let currentIndex = 0;

  // Format common structure (first 7 characters: sexe, année, mois, département)
  const commonResult = formatCommonStructure(cleaned, currentIndex);
  allParts.push(...commonResult.parts);
  currentIndex = commonResult.nextIndex;

  // Format the grouped section if applicable
  if (cleaned.length >= FORMAT_CONSTANTS.MIN_LENGTH_FOR_FORMATTING) {
    const dept = cleaned.slice(FORMAT_CONSTANTS.DEPT_START, FORMAT_CONSTANTS.DEPT_START + FORMAT_CONSTANTS.DEPT_LENGTH);
    const remainingAfterDept = cleaned.length - currentIndex;
    const skipSocialSecurityNumberFormatting = shouldSkipSocialSecurityNumberFormatting(
      dept,
      remainingAfterDept,
      cleaned.length
    );

    if (!skipSocialSecurityNumberFormatting) {
      const socialSecurityNumberSection = formatSocialSecurityNumberSection(
        cleaned,
        currentIndex,
        FORMAT_CONSTANTS.FULL_LENGTH
      );
      allParts.push(...socialSecurityNumberSection.parts);
      currentIndex = socialSecurityNumberSection.nextIndex;
    }
  }

  // Format remaining characters
  if (cleaned.length > currentIndex) {
    const rest = cleaned.slice(currentIndex);
    const formattedRest = formatRemainingChars(rest, cleaned.length);
    allParts.push(formattedRest);
  }

  return allParts.join(" ");
}
